import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { FiChevronDown, FiInfo } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { accent, font, fontSize, neutral, radius, shadow, text } from 'styles/revampTokens';

import {
  taskFieldProperties,
  taskOutlineButtonProperties,
  taskPrimaryButtonProperties,
  taskQuietButtonProperties,
} from './taskControls';
import { formatDue, statusLabel, statusTone } from './format';
import { planName } from './groupTasks';
import {
  getTask,
  maestroErrorMessage,
  saveInputs,
  saveWorkspaceState,
  submitTask,
} from './maestroClient';
import { ModelPerson } from './modelPeople';
import {
  answersOf,
  cellsOf,
  constantText,
  figureFor,
  findCell,
  hasTimeColumns,
  periodLabels,
  rowsForGrid,
} from './sheet';
import TaskActions from './TaskActions';
import ConversationPanel, { ConversationToggles } from './TaskConversation';
import TaskGrid, { TaskGridRow } from './TaskGrid';
import { pageTitle, PlanningPage, StepTabs, TasksViewer } from './TasksChrome';
import {
  ActivityEvent,
  HubTask,
  TaskComment,
  TaskWorkspace as Workspace,
  WorkspaceAnswers,
  WorkspaceBlock,
  WorkspaceSpec,
  emptyCapabilities,
} from './types';

/** The heading row. It can render before the plan numbers exist. */
export interface TaskShell {
  plan: string;
  cycle: string;
  item: string;
  description: string;
  period: string;
  /** "Baseline • Jan 25 to Dec 28", once the model time range is known. */
  context?: string;
  dueAt: string | null;
  overdue: boolean;
  status: string;
  reopenNote?: string | null;
}

export const shellFromTask = (task: {
  title: string;
  description?: string;
  cycle_label?: string;
  dimension_item_name: string;
  period: string;
  due_at: string | null;
  overdue: boolean;
  status: string;
  reopen_note?: string | null;
}): TaskShell => ({
  plan: planName(task.title, task.dimension_item_name),
  cycle: task.cycle_label || '',
  item: task.dimension_item_name,
  description: task.description || '',
  period: task.period,
  dueAt: task.due_at,
  overdue: task.overdue,
  status: task.status,
  reopenNote: task.reopen_note,
});

interface TaskWorkspaceProperties {
  workspace: Workspace | null;
  shell?: TaskShell | null;
  heldSpec?: WorkspaceSpec | null;
  siblings: HubTask[];
  onWorkspace: (next: Workspace) => void;
  viewer?: TasksViewer;
  comments?: TaskComment[];
  activity?: ActivityEvent[];
  people?: Record<number, string>;
  onComment?: (body: string) => Promise<void>;
  onReassign?: (assigneeUserIds: number[]) => Promise<void>;
  onApprove?: (note: string) => Promise<void>;
  onReopen?: (note: string) => Promise<void>;
  onClose?: () => Promise<void>;
  onChangeDue?: (dueAt: string) => Promise<void>;
  loadModelPeople?: () => Promise<ModelPerson[]>;
}

const isNumericValue = (value: number | string | null | undefined) => {
  if (value == null || value === '') return true;
  if (typeof value === 'number') return true;
  return !Number.isNaN(Number(value));
};

const BlockView = ({
  block,
  cells,
  answers,
  editable,
  busy,
  rows,
  columnTitles,
  freezeColumns,
  onAnswers,
  onGridEdit,
}: {
  block: WorkspaceBlock;
  cells: ReturnType<typeof cellsOf>;
  answers: WorkspaceAnswers;
  editable: boolean;
  busy: boolean;
  rows: TaskGridRow[];
  columnTitles: string[];
  freezeColumns: number;
  onAnswers: (next: WorkspaceAnswers) => void;
  onGridEdit: (row: number, column: number, value: number | string | null) => void;
}) => {
  if (block.kind === 'help' && block.text) {
    return (
      <Box
        p='12px 14px'
        bg={accent.subtle}
        border={`1px solid ${accent.border}`}
        borderRadius={radius.surface}
      >
        <Text fontSize={fontSize.body} color={text.primary}>
          {block.text}
        </Text>
      </Box>
    );
  }
  if (block.kind === 'metrics') {
    return (
      <Flex gap={3} wrap='wrap'>
        {(block.items ?? []).map((item) => (
          <Box
            key={item.label}
            minW='160px'
            p={4}
            bg={accent.subtle}
            border={`1px solid ${accent.border}`}
            borderLeft={`3px solid ${accent.solid}`}
            borderRadius={radius.surface}
          >
            <Text fontSize={fontSize.label} color={text.secondary}>
              {item.label}
            </Text>
            <Text mt={2} fontSize={fontSize.subtitle} color={text.primary} fontWeight={600}>
              {item.indicator_or_column
                ? figureFor(cells, item.indicator_or_column)
                : constantText(item.constant, item.display)}
            </Text>
          </Box>
        ))}
      </Flex>
    );
  }
  if (block.kind === 'grid') {
    return (
      <TaskGrid
        label={block.label || 'Plan'}
        columnTitles={columnTitles}
        freezeColumns={freezeColumns}
        rows={rows}
        disabled={!editable || busy}
        onEdit={onGridEdit}
      />
    );
  }
  if (block.kind === 'summary') {
    return (
      <Box>
        {(block.lines ?? []).map((line) => (
          <Flex
            key={line.label}
            justify='space-between'
            py={2}
            borderBottom={`1px solid ${neutral.border}`}
          >
            <Text fontSize={fontSize.body} color={text.secondary}>
              {line.label}
            </Text>
            <Text
              fontSize={fontSize.body}
              color={text.primary}
              fontWeight={600}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {figureFor(cells, line.indicator_or_column)}
            </Text>
          </Flex>
        ))}
      </Box>
    );
  }
  if (block.kind === 'assumptions' || block.kind === 'commentary') {
    const field = block.kind === 'assumptions' ? 'assumptions' : 'commentary';
    return (
      <Box>
        <Text fontSize={fontSize.label} fontWeight={600} color={text.secondary} mb={2}>
          {block.label}
        </Text>
        <Textarea
          aria-label={block.label || field}
          value={answers[field]}
          placeholder={block.placeholder || ''}
          isReadOnly={!editable}
          {...taskFieldProperties}
          onChange={(event) => onAnswers({ ...answers, [field]: event.target.value })}
        />
      </Box>
    );
  }
  const { flag, kind, label } = block;
  if (kind === 'confirm' && flag) {
    return (
      <Checkbox
        isChecked={Boolean(answers.flags[flag])}
        isDisabled={!editable}
        colorScheme='purple'
        onChange={(event) =>
          onAnswers({ ...answers, flags: { ...answers.flags, [flag]: event.target.checked } })
        }
      >
        <Text fontSize={fontSize.body} color={text.primary}>
          {label}
        </Text>
      </Checkbox>
    );
  }
  return null;
};

const TaskWorkspaceView = ({
  workspace,
  shell = null,
  heldSpec = null,
  siblings,
  onWorkspace,
  viewer,
  comments = [],
  activity = [],
  people,
  onComment,
  onReassign,
  onApprove,
  onReopen,
  onClose,
  onChangeDue,
  loadModelPeople,
}: TaskWorkspaceProperties) => {
  const navigate = useNavigate();
  const [parameters, setParameters] = useSearchParams();
  const shown = shell ?? (workspace ? shellFromTask(workspace.task) : null);
  const spec = workspace?.workspace_spec ?? heldSpec;
  const tabs = spec?.tabs ?? [];
  const requested = parameters.get('tab');
  const tab = tabs.find((item) => item.id === requested) ?? tabs[0];
  const tabIndex = Math.max(
    0,
    tabs.findIndex((item) => item.id === tab?.id),
  );
  const cells = workspace ? cellsOf(workspace) : [];
  const editable = workspace?.render.editable ?? false;
  const ready = Boolean(workspace?.view);
  const [answers, setAnswers] = useState<WorkspaceAnswers>(() =>
    workspace ? answersOf(workspace) : { flags: {}, commentary: '', assumptions: '' },
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    setAnswers(answersOf(workspace));
    setError('');
    setSaved(false);
  }, [workspace]);

  useEffect(() => {
    const item = shown?.item;
    if (item) document.title = `${shown?.plan ?? 'Task'} for ${item} — Blox`;
  }, [shown?.item, shown?.plan]);

  const setTab = (id: string) => {
    const next = new URLSearchParams(parameters);
    next.set('tab', id);
    setParameters(next);
  };

  // Opening a task with no step in the address lands on the first one.
  useEffect(() => {
    const first = tabs[0]?.id;
    if (!first) return;
    const current = parameters.get('tab');
    if (current && tabs.some((item) => item.id === current)) return;
    const next = new URLSearchParams(parameters);
    next.set('tab', first);
    setParameters(next, { replace: true });
  }, [tabs, parameters, setParameters]);

  const requestedPanel = parameters.get('panel');
  const panel =
    requestedPanel === 'comments' || requestedPanel === 'activity' ? requestedPanel : null;
  const setPanel = (next: 'comments' | 'activity') => {
    const updated = new URLSearchParams(parameters);
    if (panel === next) updated.delete('panel');
    else updated.set('panel', next);
    setParameters(updated);
  };

  const taskId = workspace?.task.id;

  const persistAnswers = async () => {
    if (!taskId) return;
    setBusy(true);
    setError('');
    try {
      const stored = await saveWorkspaceState(taskId, answers);
      setAnswers(stored);
      setSaved(true);
    } catch (saveError: unknown) {
      setError(maestroErrorMessage(saveError, 'The draft could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async () => {
    if (!taskId) return;
    setBusy(true);
    setError('');
    try {
      await saveWorkspaceState(taskId, answers);
      await submitTask(taskId);
      onWorkspace(await getTask(taskId));
    } catch (submitError: unknown) {
      setError(maestroErrorMessage(submitError, 'This task could not be submitted.'));
    } finally {
      setBusy(false);
    }
  };

  const gridLayout = (
    block: WorkspaceBlock,
  ): { titles: string[]; rows: TaskGridRow[]; freeze: number } => {
    const columns = block.columns ?? [];
    const names = new Set(columns.map((column) => column.indicator_or_column));
    const blockCells = cells.filter(
      (cell) =>
        names.has(cell.indicator_or_column) &&
        (block.block_id == null || cell.block_id == null || cell.block_id === block.block_id),
    );
    if (!hasTimeColumns(blockCells)) {
      return {
        titles: ['Item', ...columns.map((column) => column.label)],
        freeze: 1,
        rows: rowsForGrid(cells, block.block_id, columns).map((row) => ({
          id: row.key,
          cells: [
            { display: row.label, value: row.label, editable: false, numeric: false },
            ...columns.map((column) => {
              const found = findCell(
                cells,
                block.block_id,
                column.indicator_or_column,
                row.coordinates,
              );
              const columnCells = cells.filter(
                (item) => item.indicator_or_column === column.indicator_or_column,
              );
              return {
                display: found?.display ?? (found?.value == null ? '' : String(found.value)),
                value: found?.value ?? null,
                editable: editable && column.editable && found?.role !== 'calculated',
                numeric: columnCells.every((item) => isNumericValue(item.value)),
              };
            }),
          ],
        })),
      };
    }
    const dimensionKeys: string[] = [];
    blockCells.forEach((cell) => {
      Object.keys(cell.coordinates || {}).forEach((key) => {
        if (!dimensionKeys.includes(key)) dimensionKeys.push(key);
      });
    });
    const periods = periodLabels(blockCells);
    const indicatorOrder = new Map(
      columns.map((column, index) => [column.indicator_or_column, index]),
    );
    const ordered = [...blockCells].sort((left, right) => {
      const label = dimensionKeys
        .map((key) => left.coordinates?.[key] || '')
        .join('|')
        .localeCompare(dimensionKeys.map((key) => right.coordinates?.[key] || '').join('|'));
      if (label !== 0) return label;
      return (
        (indicatorOrder.get(left.indicator_or_column) ?? 0) -
        (indicatorOrder.get(right.indicator_or_column) ?? 0)
      );
    });
    return {
      titles: [...dimensionKeys, 'Indicator', ...periods],
      freeze: dimensionKeys.length + 1,
      rows: ordered.map((cell) => {
        const column = columns.find(
          (item) => item.indicator_or_column === cell.indicator_or_column,
        );
        const canEdit = editable && Boolean(column?.editable) && cell.role !== 'calculated';
        return {
          id: `${cell.block_id ?? ''}|${cell.indicator_or_column}|${dimensionKeys
            .map((key) => cell.coordinates?.[key] || '')
            .join('|')}`,
          cells: [
            ...dimensionKeys.map((key) => ({
              display: cell.coordinates?.[key] || '',
              value: cell.coordinates?.[key] || '',
              editable: false,
              numeric: false,
            })),
            {
              display: column?.label || cell.indicator_or_column,
              value: column?.label || cell.indicator_or_column,
              editable: false,
              numeric: false,
            },
            ...periods.map((period) => {
              const raw = cell.periods?.[period];
              const numeric =
                raw == null || raw === '' || typeof raw === 'number' || !Number.isNaN(Number(raw));
              return {
                display: raw == null || raw === '' ? '' : String(raw),
                value:
                  raw == null || raw === ''
                    ? null
                    : typeof raw === 'number'
                    ? raw
                    : Number.isNaN(Number(raw))
                    ? raw
                    : Number(raw),
                editable: canEdit,
                numeric: numeric && raw != null && raw !== '',
                period,
                indicator: cell.indicator_or_column,
                coordinates: cell.coordinates || {},
              };
            }),
          ],
        };
      }),
    };
  };

  const onGridEdit = async (
    block: WorkspaceBlock,
    rowIndex: number,
    columnIndex: number,
    value: number | string | null,
  ) => {
    const layout = gridLayout(block);
    const target = layout.rows[rowIndex]?.cells[columnIndex];
    if (!editable || !target?.editable || !taskId) return;
    const columns = block.columns ?? [];
    const { indicator, coordinates, period } = target;
    if (!indicator || !coordinates) {
      const rows = rowsForGrid(cells, block.block_id, columns);
      const row = rows[rowIndex];
      const column = columns[columnIndex - 1];
      if (!row || !column || !column.editable) return;
      setBusy(true);
      setError('');
      try {
        const next = await saveInputs(taskId, [
          {
            indicator_or_column: column.indicator_or_column,
            coordinates: row.coordinates,
            value,
            block_id: block.block_id ?? null,
          },
        ]);
        onWorkspace(next);
      } catch (editError: unknown) {
        setError(maestroErrorMessage(editError, 'That cell could not be saved.'));
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    setError('');
    try {
      const next = await saveInputs(taskId, [
        {
          indicator_or_column: indicator,
          coordinates: period ? { ...coordinates, Time: period } : coordinates,
          value,
          block_id: block.block_id ?? null,
        },
      ]);
      onWorkspace(next);
    } catch (editError: unknown) {
      setError(maestroErrorMessage(editError, 'That cell could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const statusTint = statusTone(shown?.status ?? 'not_started');
  const canSwitch = siblings.length > 1 && Boolean(shown);

  return (
    <PlanningPage>
      <Flex direction='column' flex='1' minH={0}>
        <Box flexShrink={0}>
          <Flex justify='space-between' gap={4} align='flex-start' wrap='wrap'>
            <Box flex='1' minW='min-content'>
              {shown && canSwitch ? (
                <Box {...pageTitle} whiteSpace='nowrap'>
                  <Menu>
                    {/* size slider has no fixed width. The default button is 288px, which wrapped the chevron. */}
                    <MenuButton
                      as={Button}
                      variant='unstyled'
                      size='slider'
                      aria-haspopup='menu'
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        w: 'max-content',
                        maxW: 'none',
                        h: 'auto',
                        minW: 'max-content',
                        p: 0,
                        bg: 'transparent',
                        border: 'none',
                        borderRadius: 0,
                        cursor: 'pointer',
                        fontFamily: font.display,
                        fontSize: '22px',
                        fontWeight: 600,
                        lineHeight: '1.2',
                        color: text.primary,
                        whiteSpace: 'nowrap',
                        // MenuButton wraps the label in a span that is allowed to shrink,
                        // which dropped the chevron onto the next line.
                        '& > span': {
                          display: 'inline-flex',
                          alignItems: 'center',
                          flex: '0 0 auto',
                          minW: 'max-content',
                          whiteSpace: 'nowrap',
                        },
                        _hover: { bg: 'transparent', color: text.primary },
                        _active: { bg: 'transparent' },
                      }}
                    >
                      <Box as='span' display='inline-flex' alignItems='center' whiteSpace='nowrap'>
                        {`${shown.plan} for ${shown.item}`}
                        <Box
                          as={FiChevronDown}
                          display='inline-block'
                          boxSize='22px'
                          ml='6px'
                          flexShrink={0}
                          color={text.secondary}
                          aria-hidden
                        />
                      </Box>
                    </MenuButton>
                    <MenuList
                      bg={neutral.surface}
                      borderColor={neutral.border}
                      boxShadow={shadow.panel}
                      w='320px'
                      minW='320px'
                      maxH='280px'
                      overflowY='auto'
                      zIndex={30}
                      py={1}
                    >
                      {siblings.map((item) => {
                        const tone = statusTone(item.status);
                        const current = item.dimension_item_name === shown.item;
                        return (
                          <MenuItem
                            key={item.id}
                            display='grid'
                            gridTemplateColumns='minmax(0, 1fr) auto'
                            alignItems='center'
                            columnGap='12px'
                            w='100%'
                            minH='36px'
                            h='auto'
                            px='12px'
                            py='6px'
                            bg={current ? accent.subtle : neutral.surface}
                            color={text.primary}
                            fontSize='13px'
                            _hover={{ bg: neutral.surfaceHover }}
                            _focus={{ bg: neutral.surfaceHover }}
                            sx={{ width: '100%', minWidth: '100%' }}
                            onClick={() =>
                              navigate(`/tasks/${item.id}?tab=${tab?.id ?? tabs[0]?.id ?? ''}`)
                            }
                          >
                            <Text noOfLines={1} fontWeight={current ? 600 : 400} textAlign='left'>
                              {item.dimension_item_name}
                            </Text>
                            <Text
                              justifySelf='end'
                              px='6px'
                              py='1px'
                              borderRadius='4px'
                              bg={tone.bg}
                              color={tone.color}
                              fontSize='11px'
                              fontWeight={600}
                              whiteSpace='nowrap'
                            >
                              {statusLabel(item.status)}
                            </Text>
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </Menu>
                </Box>
              ) : (
                <Text {...pageTitle}>
                  {shown ? `${shown.plan} for ${shown.item}` : 'Opening task'}
                </Text>
              )}
              <Text mt='4px' minH='18px' fontSize='13px' color={text.secondary}>
                {shown?.description || (ready ? '' : 'Loading the task…')}
              </Text>
            </Box>
            <Flex align='center' gap={2} flexShrink={0}>
              {workspace && (
                <TaskActions
                  variant='menu'
                  capabilities={workspace.capabilities ?? emptyCapabilities}
                  assigneeUserIds={workspace.task.assignee_user_ids}
                  loadModelPeople={loadModelPeople}
                  onReassign={onReassign ?? (async () => {})}
                  onApprove={onApprove ?? (async () => {})}
                  onReopen={onReopen ?? (async () => {})}
                  onClose={onClose ?? (async () => {})}
                  onChangeDue={onChangeDue ?? (async () => {})}
                />
              )}
              {shown && (
                <Text
                  px='8px'
                  py='4px'
                  borderRadius='4px'
                  bg={shown.overdue ? '#FFF4E5' : '#F2F3F5'}
                  color={shown.overdue ? '#8A4B08' : '#5B6472'}
                  fontSize='11px'
                  fontWeight={600}
                  whiteSpace='nowrap'
                >
                  Due {formatDue(shown.dueAt)}
                </Text>
              )}
              <Text
                px='8px'
                py='4px'
                borderRadius='4px'
                bg={statusTint.bg}
                color={statusTint.color}
                fontSize='11px'
                fontWeight={600}
                whiteSpace='nowrap'
              >
                {statusLabel(shown?.status ?? 'not_started')}
              </Text>
            </Flex>
          </Flex>
          {shown?.context && (
            <Text mt='10px' minH='18px' fontSize='13px' color={text.secondary}>
              {shown.context}
            </Text>
          )}
          {shown?.reopenNote && (
            <Box mt={4} p={3} border={`1px solid ${neutral.border}`} borderRadius='5px'>
              <Text fontSize={fontSize.body} color={text.primary}>
                {shown.reopenNote}
              </Text>
            </Box>
          )}
          <Box
            mx={{ base: '-19px', md: '-32px' }}
            mt='12px'
            borderBottom='1px solid #D1D6DD'
            flexShrink={0}
          >
            <Flex align='center' gap={2} minH='40px' px={{ base: '19px', md: '32px' }} pb='8px'>
              {tabs.length > 0 ? (
                <StepTabs tabs={tabs} activeId={tab?.id} onSelect={setTab} />
              ) : (
                <Flex flex='1' gap='4px' aria-hidden>
                  {[0, 1, 2].map((item) => (
                    <Box
                      key={item}
                      h='32px'
                      w='108px'
                      borderRadius={radius.control}
                      bg={neutral.surfaceHover}
                    />
                  ))}
                </Flex>
              )}
              <ConversationToggles count={comments.length} panel={panel} onPanel={setPanel} />
            </Flex>
          </Box>
        </Box>
        <Flex
          flex='1'
          minH={0}
          overflow='hidden'
          direction={{ base: 'column', lg: 'row' }}
          align='stretch'
          mt={3}
        >
          <Box flex='1' minW={0} minH={0} overflow='auto' pr={panel ? { lg: '24px' } : undefined}>
            <Box bg={neutral.surface} pb={2}>
              {ready && tab && (
                <Box
                  key={workspace?.task.id}
                  sx={{
                    '@keyframes taskSettle': {
                      from: { opacity: 0.2 },
                      to: { opacity: 1 },
                    },
                    animation: 'taskSettle 320ms ease',
                  }}
                >
                  <Text
                    mt='16px'
                    fontFamily={font.display}
                    fontSize='17px'
                    fontWeight={600}
                    color={text.primary}
                  >
                    {tab.heading}
                  </Text>
                  {tab.guidance && (
                    <Flex
                      mt='12px'
                      p='12px 14px'
                      bg='#F4F7FF'
                      border='1px solid #D5E2F7'
                      borderRadius='5px'
                      align='center'
                      gap='10px'
                      fontSize='12px'
                      color={text.primary}
                    >
                      <Box as={FiInfo} color={accent.solid} flexShrink={0} aria-hidden />
                      <Text>{tab.guidance}</Text>
                    </Flex>
                  )}
                  <Flex direction='column' gap={5} mt={5}>
                    {(tab.blocks ?? []).map((block, index) => {
                      const layout = gridLayout(block);
                      return (
                        <BlockView
                          key={`${tab.id}-${index}`}
                          block={block}
                          cells={cells}
                          answers={answers}
                          editable={editable}
                          busy={busy}
                          rows={layout.rows}
                          columnTitles={layout.titles}
                          freezeColumns={layout.freeze}
                          onAnswers={setAnswers}
                          onGridEdit={(row, column, value) => onGridEdit(block, row, column, value)}
                        />
                      );
                    })}
                  </Flex>
                  {error && (
                    <Text mt={4} fontSize={fontSize.body} color={text.primary} role='alert'>
                      {error}
                    </Text>
                  )}
                  <Flex
                    mt='25px'
                    py='20px'
                    borderTop={`1px solid ${neutral.border}`}
                    justify='space-between'
                    align='center'
                    gap={3}
                    wrap='wrap'
                  >
                    <Text fontSize='11px' color={text.secondary}>
                      {editable
                        ? saved
                          ? 'Draft saved'
                          : 'Draft'
                        : statusLabel(shown?.status ?? 'not_started')}
                    </Text>
                    <Flex gap={2}>
                      {tabIndex > 0 && (
                        <Button
                          size='sm'
                          {...taskQuietButtonProperties}
                          onClick={() => setTab(tabs[tabIndex - 1].id)}
                        >
                          Back
                        </Button>
                      )}
                      {editable && (
                        <Button
                          size='sm'
                          {...taskOutlineButtonProperties}
                          isDisabled={busy}
                          onClick={persistAnswers}
                        >
                          Save draft
                        </Button>
                      )}
                      {editable && tabIndex < tabs.length - 1 && (
                        <Button
                          size='sm'
                          {...taskPrimaryButtonProperties}
                          onClick={() => setTab(tabs[tabIndex + 1].id)}
                        >
                          Continue
                        </Button>
                      )}
                      {editable && tabIndex === tabs.length - 1 && (
                        <Button
                          size='sm'
                          {...taskPrimaryButtonProperties}
                          isDisabled={busy}
                          onClick={onSubmit}
                        >
                          Submit
                        </Button>
                      )}
                      {!editable && workspace && tabIndex === tabs.length - 1 && (
                        <TaskActions
                          variant='review'
                          capabilities={workspace.capabilities ?? emptyCapabilities}
                          onReassign={onReassign ?? (async () => {})}
                          onApprove={onApprove ?? (async () => {})}
                          onReopen={onReopen ?? (async () => {})}
                          onClose={onClose ?? (async () => {})}
                          onChangeDue={onChangeDue ?? (async () => {})}
                        />
                      )}
                    </Flex>
                  </Flex>
                </Box>
              )}
              {!ready && (
                <Flex align='center' gap={3} minH='180px' color={text.secondary}>
                  <Spinner size='sm' color={accent.solid} />
                  <Text fontSize={fontSize.body}>Loading the plan…</Text>
                </Flex>
              )}
            </Box>
          </Box>
          {panel && (
            <Flex
              direction='column'
              w={{ base: '100%', lg: '340px' }}
              flexShrink={0}
              alignSelf='stretch'
              minH={0}
              overflow='auto'
              bg={neutral.canvas}
              border={`1px solid ${neutral.border}`}
              borderRadius='8px'
              pl={{ base: 3, lg: 4 }}
              pr={{ base: 3, lg: 4 }}
              pt={4}
              pb={4}
            >
              <ConversationPanel
                comments={comments}
                activity={activity}
                panel={panel}
                onAdd={onComment ?? (async () => {})}
                viewer={viewer}
                people={people}
                busy={busy}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </PlanningPage>
  );
};

export default TaskWorkspaceView;
